import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  Plus, Star, Phone, Mail, MapPin, Pencil, Trash2, Search, Clock, FileText,
  Check, ClipboardList, Heart, UserPlus, KeyRound, X,
} from "lucide-react";
import DocumentsVivier from "../components/vivier/DocumentsVivier";
import SignatureEmployeur from "../components/vivier/SignatureEmployeur";
import {
  API, JOURS, L_AUTO, L_CRIT, L_POSTE, L_STATUT, dateFr, erreur, euros, nomComplet, telecharger,
} from "../components/vivier/outils";

const VIDE = {
  prenom: "", nom: "", telephone: "", email: "", ville: "", zone: "", adresse: "",
  date_naissance: "", numero_ss: "", postes: [], autonomie: "debutant", statut: "autre",
  jours_dispo: [], commentaires: "", taux_horaire: "", actif: true,
  siret: "", raison_sociale: "", assurance_rc: "", tarif_horaire: "",
};
const ETAT = {
  propose: { txt: "En attente de validation", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  refuse: { txt: "Refusée", cls: "bg-red-50 text-red-700 border-red-300" },
  valide: { txt: "Validée", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
};

function Onglet({ actif, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${actif
        ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted"}`}>
      {children}
    </button>
  );
}

function Choix({ options, valeurs, onBascule }) {
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {Object.entries(options).map(([k, v]) => (
        <Button key={k} type="button" size="sm"
          variant={(valeurs || []).includes(k) ? "default" : "outline"}
          onClick={() => onBascule(k)}>{v}</Button>
      ))}
    </div>
  );
}

export default function VivierPage() {
  const { user } = useAuth();
  const gestion = ["admin", "bureau"].includes(user?.role);
  const admin = user?.role === "admin";
  const monFt = user?.food_truck_id || null;

  const [onglet, setOnglet] = useState("vivier");
  const [liste, setListe] = useState([]);
  const [propositions, setPropositions] = useState([]);
  const [q, setQ] = useState("");
  const [fPoste, setFPoste] = useState("");
  const [fJour, setFJour] = useState("");

  const charger = useCallback(async () => {
    try {
      const params = {};
      if (fPoste) params.poste = fPoste;
      if (fJour) params.jour = fJour;
      const [v, p] = await Promise.all([
        axios.get(`${API}/vivier`, { params }),
        axios.get(`${API}/vivier`, { params: { vue: "propositions" } }),
      ]);
      setListe(v.data || []);
      setPropositions(p.data || []);
    } catch (e) {
      toast.error(erreur(e, "Chargement impossible"));
    }
  }, [fPoste, fJour]);

  useEffect(() => { charger(); }, [charger]);

  // ----- fiche -----
  const [ficheOpen, setFicheOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(VIDE);
  const [acces, setAcces] = useState(null);
  const [mdp, setMdp] = useState("");

  const ouvrirFiche = async (item) => {
    setEdit(item || null);
    setForm(item ? { ...VIDE, ...item } : VIDE);
    setAcces(null);
    setMdp("");
    setFicheOpen(true);
    if (item && gestion && item.etat === "valide") {
      try {
        const r = await axios.get(`${API}/vivier/${item.id}/acces`);
        setAcces(r.data);
      } catch (e) { /* pas d'acces */ }
    }
  };

  const bascule = (champ, val) => setForm((f) => ({
    ...f,
    [champ]: (f[champ] || []).includes(val) ? f[champ].filter((x) => x !== val) : [...(f[champ] || []), val],
  }));

  const enregistrerFiche = async () => {
    if (!form.nom.trim() && !form.prenom.trim()) return toast.error("Nom ou prénom requis");
    try {
      if (edit) await axios.put(`${API}/vivier/${edit.id}`, form);
      else await axios.post(`${API}/vivier`, form);
      toast.success(edit ? "Fiche enregistrée"
        : gestion ? "Ajoutée au vivier" : "Proposition envoyée au bureau pour validation");
      setFicheOpen(false);
      if (!edit && !gestion) setOnglet("propositions");
      charger();
    } catch (e) {
      toast.error(erreur(e, "Enregistrement impossible"));
    }
  };

  const creerAcces = async () => {
    try {
      const r = await axios.put(`${API}/vivier/${edit.id}/acces`, { email: form.email, password: mdp });
      setAcces(r.data);
      setMdp("");
      toast.success("Accès enregistré : transmettez l'email et le mot de passe à l'extra");
    } catch (e) {
      toast.error(erreur(e, "Création de l'accès impossible"));
    }
  };

  const supprimer = async (p) => {
    if (!window.confirm(`Supprimer la fiche de ${nomComplet(p)} ?`)) return;
    try {
      await axios.delete(`${API}/vivier/${p.id}`);
      toast.success("Fiche supprimée");
      charger();
    } catch (e) {
      toast.error(erreur(e, "Suppression impossible"));
    }
  };

  const favori = async (p) => {
    try {
      await axios.post(`${API}/vivier/${p.id}/favori`);
      charger();
    } catch (e) {
      toast.error(erreur(e, "Action impossible"));
    }
  };

  // ----- validation -----
  const [refus, setRefus] = useState(null);
  const [motif, setMotif] = useState("");
  const decider = async (p, decision, m = "") => {
    try {
      await axios.post(`${API}/vivier/${p.id}/validation`, { decision, motif: m });
      toast.success(decision === "valider" ? "Ajoutée au vivier" : "Proposition refusée");
      setRefus(null);
      setMotif("");
      charger();
    } catch (e) {
      toast.error(erreur(e, "Action impossible"));
    }
  };

  // ----- missions -----
  const [misOpen, setMisOpen] = useState(null);
  const [missions, setMissions] = useState(null);
  const [camions, setCamions] = useState([]);
  const [mForm, setMForm] = useState({});

  const chargerMissions = async (p) => {
    try {
      const r = await axios.get(`${API}/vivier/${p.id}/missions`);
      setMissions(r.data);
    } catch (e) {
      toast.error(erreur(e, "Chargement impossible"));
    }
  };

  const ouvrirMissions = async (p) => {
    setMisOpen(p);
    setMissions(null);
    setMForm({
      date: new Date().toISOString().split("T")[0], heure_debut: "", heure_fin: "",
      pause_min: 0, lieu: "", food_truck_id: monFt || "",
      taux_horaire: (p.statut === "auto_entrepreneur" ? p.tarif_horaire : p.taux_horaire) || "",
    });
    chargerMissions(p);
    if (gestion && camions.length === 0) {
      try {
        const r = await axios.get(`${API}/vivier/camions`);
        setCamions(r.data || []);
      } catch (e) { /* liste vide */ }
    }
  };

  const ajouterMission = async () => {
    if (!mForm.date) return toast.error("Date requise");
    if (!mForm.food_truck_id && gestion && !monFt) return toast.error("Choisissez le food truck employeur");
    try {
      await axios.post(`${API}/vivier/${misOpen.id}/missions`, mForm);
      toast.success("Mission enregistrée : l'extra peut maintenant signer son contrat");
      setMForm((f) => ({ ...f, heure_debut: "", heure_fin: "", lieu: "" }));
      chargerMissions(misOpen);
    } catch (e) {
      toast.error(erreur(e, "Enregistrement impossible"));
    }
  };

  const basculerPaye = async (m) => {
    try {
      await axios.put(`${API}/vivier/missions/${m.id}`, { paye: !m.paye });
      chargerMissions(misOpen);
    } catch (e) {
      toast.error(erreur(e, "Action impossible"));
    }
  };

  const supprimerMission = async (m) => {
    if (!window.confirm("Supprimer cette mission ?")) return;
    try {
      await axios.delete(`${API}/vivier/missions/${m.id}`);
      chargerMissions(misOpen);
    } catch (e) {
      toast.error(erreur(e, "Suppression impossible"));
    }
  };

  // ----- evaluation -----
  const [evalOpen, setEvalOpen] = useState(null);
  const [notes, setNotes] = useState({});
  const noter = async () => {
    try {
      await axios.post(`${API}/vivier/${evalOpen.id}/evaluation`, notes);
      toast.success("Évaluation enregistrée");
      setEvalOpen(null);
      setNotes({});
      charger();
    } catch (e) {
      toast.error(erreur(e, "Évaluation impossible"));
    }
  };

  // ----- recap et vigilance -----
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [mois, setMois] = useState("");
  const [recap, setRecap] = useState(null);
  const [vigilance, setVigilance] = useState(null);
  useEffect(() => {
    if (onglet !== "recap") return;
    const params = { annee };
    if (mois) params.mois = mois;
    Promise.all([
      axios.get(`${API}/vivier/recap`, { params }),
      axios.get(`${API}/vivier/vigilance`, { params: { annee } }),
    ]).then(([r, v]) => { setRecap(r.data); setVigilance(v.data); })
      .catch((e) => toast.error(erreur(e, "Chargement impossible")));
  }, [onglet, annee, mois]);

  const filtres = liste.filter((p) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [p.nom, p.prenom, p.ville, p.zone, p.telephone, p.email]
      .filter(Boolean).join(" ").toLowerCase().includes(s);
  });
  const enAttente = propositions.filter((p) => p.etat === "propose").length;
  const ae = form.statut === "auto_entrepreneur";
  const peutModifier = (p) => gestion || (p.propose_par_moi && p.etat !== "valide");

  const carte = (p, modePropositions) => (
    <Card key={p.id} className={p.actif === false ? "opacity-60" : ""}>
      <CardContent className="p-4 space-y-2.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold truncate">{nomComplet(p)}</p>
            <p className="text-xs text-muted-foreground">
              {L_AUTO[p.autonomie] || p.autonomie}{p.statut ? ` · ${L_STATUT[p.statut] || p.statut}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {p.note_globale != null && (
              <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-800">
                <Star className="w-3 h-3 mr-1 fill-amber-500 text-amber-500" />
                {String(p.note_globale).replace(".", ",")}
                <span className="ml-1 opacity-60">({p.nb_evaluations || 0})</span>
              </Badge>
            )}
            {monFt && !modePropositions && (
              <Button variant="ghost" size="icon" className="h-7 w-7" title="Favori de mon food truck"
                onClick={() => favori(p)}>
                <Heart className={`w-4 h-4 ${p.favori ? "fill-red-500 text-red-500" : ""}`} />
              </Button>
            )}
          </div>
        </div>

        {modePropositions && (
          <div className="space-y-1">
            <Badge variant="outline" className={ETAT[p.etat]?.cls}>{ETAT[p.etat]?.txt}</Badge>
            {p.propose_par_nom && (
              <p className="text-xs text-muted-foreground">Proposée par {p.propose_par_nom}</p>
            )}
            {p.etat === "refuse" && p.motif_refus && (
              <p className="text-xs text-red-700">Motif : {p.motif_refus}</p>
            )}
          </div>
        )}

        {(p.postes || []).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {p.postes.map((x) => <Badge key={x} variant="outline" className="text-[10px]">{L_POSTE[x] || x}</Badge>)}
          </div>
        )}

        <div className="text-sm space-y-0.5">
          {p.telephone && (
            <a href={`tel:${p.telephone}`} className="flex items-center gap-1.5 text-primary">
              <Phone className="w-3.5 h-3.5" /> {p.telephone}
            </a>
          )}
          {p.email && (
            <span className="flex items-center gap-1.5 text-muted-foreground truncate">
              <Mail className="w-3.5 h-3.5 shrink-0" /> {p.email}
            </span>
          )}
          {(p.ville || p.zone) && (
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <MapPin className="w-3.5 h-3.5" /> {[p.ville, p.zone].filter(Boolean).join(" · ")}
            </span>
          )}
        </div>

        {(p.jours_dispo || []).length > 0 && (
          <p className="text-xs"><span className="text-muted-foreground">Dispo : </span>
            {p.jours_dispo.map((j) => j.slice(0, 3)).join(", ")}</p>
        )}
        {gestion && p.commentaires && (
          <p className="text-xs text-muted-foreground italic line-clamp-2">{p.commentaires}</p>
        )}

        <div className="flex flex-wrap gap-1 pt-1 border-t">
          {modePropositions && gestion && p.etat === "propose" && (
            <>
              <Button size="sm" className="h-7 text-xs" onClick={() => decider(p, "valider")}>
                <Check className="w-3.5 h-3.5 mr-1" /> Valider
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setRefus(p); setMotif(""); }}>
                <X className="w-3.5 h-3.5 mr-1" /> Refuser
              </Button>
            </>
          )}
          {!modePropositions && (
            <>
              {(monFt || gestion) && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => ouvrirMissions(p)}>
                  <Clock className="w-3.5 h-3.5 mr-1" /> Missions
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-7 text-xs"
                onClick={() => { setEvalOpen(p); setNotes({}); }}>
                <Star className="w-3.5 h-3.5 mr-1" /> Noter
              </Button>
            </>
          )}
          <DocumentsVivier personne={p} peutDeposer={gestion || p.propose_par_moi} />
          {peutModifier(p) && (
            <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto" title="Modifier" onClick={() => ouvrirFiche(p)}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
          )}
          {(admin || (p.propose_par_moi && p.etat !== "valide")) && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" title="Supprimer" onClick={() => supprimer(p)}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="vivier-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-secondary">Vivier d'extras</h1>
          <p className="text-sm text-muted-foreground">
            Les extras partagés par les food trucks de l'association. Chaque food truck reste
            l'employeur de ses propres missions.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {monFt && <SignatureEmployeur />}
          <Button onClick={() => ouvrirFiche(null)} data-testid="ajout-vivier">
            {gestion ? <Plus className="w-4 h-4 mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
            {gestion ? "Ajouter un extra" : "Proposer un extra"}
          </Button>
        </div>
      </div>

      <div className="flex gap-1 flex-wrap">
        <Onglet actif={onglet === "vivier"} onClick={() => setOnglet("vivier")}>Vivier ({liste.length})</Onglet>
        <Onglet actif={onglet === "propositions"} onClick={() => setOnglet("propositions")}>
          {gestion ? "Propositions à valider" : "Mes propositions"}{enAttente ? ` (${enAttente})` : ""}
        </Onglet>
        <Onglet actif={onglet === "recap"} onClick={() => setOnglet("recap")}>Récap et vigilance</Onglet>
      </div>

      {onglet === "vivier" && (
        <>
          <Card>
            <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder="Nom, ville, téléphone..." />
              </div>
              <select className="h-9 border rounded-md px-2 text-sm" value={fPoste} onChange={(e) => setFPoste(e.target.value)}>
                <option value="">Tous les postes</option>
                {Object.entries(L_POSTE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select className="h-9 border rounded-md px-2 text-sm" value={fJour} onChange={(e) => setFJour(e.target.value)}>
                <option value="">Tous les jours</option>
                {JOURS.map((j) => <option key={j} value={j}>{j.charAt(0).toUpperCase() + j.slice(1)}</option>)}
              </select>
            </CardContent>
          </Card>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {filtres.length === 0 ? (
              <Card className="md:col-span-2 lg:col-span-3">
                <CardContent className="p-8 text-center text-muted-foreground">
                  Aucun extra dans le vivier pour ces critères.
                </CardContent>
              </Card>
            ) : filtres.map((p) => carte(p, false))}
          </div>
        </>
      )}

      {onglet === "propositions" && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {propositions.length === 0 ? (
            <Card className="md:col-span-2 lg:col-span-3">
              <CardContent className="p-8 text-center text-muted-foreground">
                {gestion ? "Aucune proposition en attente." : "Vous n'avez proposé aucun extra pour le moment."}
              </CardContent>
            </Card>
          ) : propositions.map((p) => carte(p, true))}
        </div>
      )}

      {onglet === "recap" && (
        <div className="space-y-4">
          <div className="flex gap-2 items-end flex-wrap">
            <div><Label className="text-xs">Année</Label>
              <Input type="number" className="w-28" value={annee} onChange={(e) => setAnnee(parseInt(e.target.value, 10) || new Date().getFullYear())} /></div>
            <div><Label className="text-xs">Mois</Label>
              <select className="h-9 border rounded-md px-2 text-sm block" value={mois} onChange={(e) => setMois(e.target.value)}>
                <option value="">Toute l'année</option>
                {["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"]
                  .map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select></div>
          </div>
          {recap && (
            <Card><CardContent className="p-4 space-y-3">
              <div className="flex flex-wrap gap-4 text-sm">
                <span>Heures : <strong>{String(recap.total_heures).replace(".", ",")} h</strong></span>
                <span>Montant : <strong>{euros(recap.total_montant)}</strong></span>
                <span className={recap.total_reste > 0 ? "text-red-600" : "text-emerald-700"}>
                  Reste à payer : <strong>{euros(recap.total_reste)}</strong></span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-2">Extra</th>{gestion && <th className="pr-2">Food truck</th>}
                    <th className="pr-2">Missions</th><th className="pr-2">Heures</th><th className="pr-2">Montant</th><th>Reste</th>
                  </tr></thead>
                  <tbody>
                    {recap.lignes.length === 0 && (
                      <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">Aucune mission sur la période.</td></tr>
                    )}
                    {recap.lignes.map((l) => (
                      <tr key={`${l.vivier_id}-${l.food_truck_id}`} className="border-b last:border-0">
                        <td className="py-2 pr-2">{l.nom}</td>{gestion && <td className="pr-2">{l.food_truck_nom}</td>}
                        <td className="pr-2">{l.nb_missions}</td><td className="pr-2">{String(l.heures).replace(".", ",")} h</td>
                        <td className="pr-2">{euros(l.montant)}</td>
                        <td className={l.reste_a_payer > 0 ? "text-red-600" : ""}>{euros(l.reste_a_payer)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent></Card>
          )}
          {vigilance && (
            <Card><CardContent className="p-4 space-y-2">
              <p className="font-semibold text-secondary">Vigilance URSSAF {vigilance.annee}</p>
              <p className="text-xs text-muted-foreground">
                L'attestation de vigilance est exigée dès que les prestations d'un même auto-entrepreneur
                atteignent {euros(vigilance.seuil)} HT sur l'année pour un même food truck. Elle se renouvelle tous les six mois.
              </p>
              {vigilance.prestataires.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun auto-entrepreneur missionné cette année.</p>
              ) : vigilance.prestataires.map((v) => (
                <div key={`${v.vivier_id}-${v.food_truck_id}`} className="flex items-center justify-between border rounded-md p-2 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{v.nom}{gestion ? ` · ${v.food_truck_nom}` : ""}</p>
                    <p className="text-xs text-muted-foreground">Cumul : {euros(v.cumul_annuel)}{v.date_expiration ? ` · expire le ${dateFr(v.date_expiration)}` : ""}</p>
                  </div>
                  <Badge variant="outline" className={{
                    manquante: "bg-red-50 text-red-700 border-red-300",
                    expiree: "bg-red-50 text-red-700 border-red-300",
                    valide: "bg-emerald-50 text-emerald-700 border-emerald-300",
                  }[v.niveau] || ""}>
                    {{ manquante: "À réclamer", expiree: "Expirée", valide: "À jour", non_exigible: "Non exigible" }[v.niveau]}
                  </Badge>
                </div>
              ))}
            </CardContent></Card>
          )}
        </div>
      )}

      {/* ----- Fiche ----- */}
      <Dialog open={ficheOpen} onOpenChange={setFicheOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{edit ? `Fiche de ${nomComplet(edit)}` : gestion ? "Nouvel extra" : "Proposer un extra au vivier"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!gestion && !edit && (
              <p className="text-sm text-muted-foreground">
                Le bureau valide chaque proposition avant qu'elle n'entre dans le vivier commun.
                En attendant, vous la retrouvez dans « Mes propositions ».
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prénom</Label><Input value={form.prenom} onChange={(e) => setForm((f) => ({ ...f, prenom: e.target.value }))} /></div>
              <div><Label>Nom</Label><Input value={form.nom} onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))} /></div>
              <div><Label>Téléphone</Label><Input value={form.telephone} onChange={(e) => setForm((f) => ({ ...f, telephone: e.target.value }))} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
              <div><Label>Ville</Label><Input value={form.ville} onChange={(e) => setForm((f) => ({ ...f, ville: e.target.value }))} /></div>
              <div><Label>Zone</Label><Input value={form.zone} placeholder="Saclay, Massy..." onChange={(e) => setForm((f) => ({ ...f, zone: e.target.value }))} /></div>
            </div>
            <div><Label>Postes tenus</Label>
              <Choix options={L_POSTE} valeurs={form.postes} onBascule={(k) => bascule("postes", k)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Autonomie</Label>
                <select className="w-full h-9 border rounded-md px-2 text-sm" value={form.autonomie} onChange={(e) => setForm((f) => ({ ...f, autonomie: e.target.value }))}>
                  {Object.entries(L_AUTO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select></div>
              <div><Label>Statut</Label>
                <select className="w-full h-9 border rounded-md px-2 text-sm" value={form.statut} onChange={(e) => setForm((f) => ({ ...f, statut: e.target.value }))}>
                  {Object.entries(L_STATUT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select></div>
            </div>
            <div><Label>Jours disponibles</Label>
              <Choix options={Object.fromEntries(JOURS.map((j) => [j, j.slice(0, 3)]))} valeurs={form.jours_dispo} onBascule={(k) => bascule("jours_dispo", k)} /></div>
            {ae ? (
              <div className="border rounded-md p-3 space-y-3 bg-muted/30">
                <p className="text-sm font-medium">Prestataire indépendant</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>SIRET</Label><Input value={form.siret} placeholder="14 chiffres" onChange={(e) => setForm((f) => ({ ...f, siret: e.target.value }))} /></div>
                  <div><Label>Raison sociale</Label><Input value={form.raison_sociale} onChange={(e) => setForm((f) => ({ ...f, raison_sociale: e.target.value }))} /></div>
                  <div><Label>Assurance RC pro</Label><Input value={form.assurance_rc} placeholder="N° de police" onChange={(e) => setForm((f) => ({ ...f, assurance_rc: e.target.value }))} /></div>
                  <div><Label>Tarif horaire facturé (€)</Label><Input type="number" value={form.tarif_horaire} onChange={(e) => setForm((f) => ({ ...f, tarif_horaire: e.target.value }))} /></div>
                </div>
                <p className="text-xs text-muted-foreground">Le SIRET est obligatoire pour éditer le contrat de prestation et les bons de commande.</p>
              </div>
            ) : (
              <div><Label>Taux horaire brut indicatif (€)</Label>
                <Input type="number" value={form.taux_horaire} onChange={(e) => setForm((f) => ({ ...f, taux_horaire: e.target.value }))} /></div>
            )}
            <div className="border rounded-md p-3 space-y-3">
              <p className="text-sm font-medium">Données du contrat</p>
              <p className="text-xs text-muted-foreground">
                Visibles uniquement par le bureau, l'extra et le food truck qui l'a proposé. Elles sont
                imprimées sur le contrat de chaque employeur. L'extra peut aussi les remplir lui-même dans son espace.
              </p>
              <div><Label>Adresse</Label><Input value={form.adresse || ""} onChange={(e) => setForm((f) => ({ ...f, adresse: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date de naissance</Label><Input type="date" value={form.date_naissance || ""} onChange={(e) => setForm((f) => ({ ...f, date_naissance: e.target.value }))} /></div>
                <div><Label>N° de sécurité sociale</Label><Input value={form.numero_ss || ""} onChange={(e) => setForm((f) => ({ ...f, numero_ss: e.target.value }))} /></div>
              </div>
            </div>
            {gestion && (
              <>
                <div><Label>Commentaires internes (bureau)</Label>
                  <Textarea rows={2} value={form.commentaires || ""} onChange={(e) => setForm((f) => ({ ...f, commentaires: e.target.value }))} /></div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.actif !== false} onChange={(e) => setForm((f) => ({ ...f, actif: e.target.checked }))} />
                  Fiche active (visible et missionnable)
                </label>
              </>
            )}
            {gestion && edit && edit.etat === "valide" && (
              <div className="border rounded-md p-3 space-y-2 bg-muted/30">
                <p className="text-sm font-medium flex items-center gap-1"><KeyRound className="w-4 h-4" /> Accès de l'extra à l'application</p>
                {acces?.compte ? (
                  <p className="text-xs text-emerald-700">Compte actif : {acces.compte.email}. Saisissez un mot de passe pour le réinitialiser.</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    L'extra pourra se connecter avec l'email de sa fiche pour compléter ses données, déposer ses pièces
                    et signer ses contrats.
                  </p>
                )}
                <div className="flex gap-2">
                  <Input type="text" placeholder="Mot de passe (10 caractères minimum)" value={mdp} onChange={(e) => setMdp(e.target.value)} />
                  <Button type="button" variant="outline" disabled={mdp.length < 10} onClick={creerAcces}>
                    {acces?.compte ? "Réinitialiser" : "Créer l'accès"}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setFicheOpen(false)}>Annuler</Button>
            <Button onClick={enregistrerFiche}>{edit ? "Enregistrer" : gestion ? "Ajouter au vivier" : "Envoyer la proposition"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- Refus ----- */}
      <Dialog open={!!refus} onOpenChange={(o) => { if (!o) setRefus(null); }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Refuser la proposition</DialogTitle></DialogHeader>
          <div><Label>Motif (transmis au food truck)</Label>
            <Textarea rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Doublon, informations incomplètes..." /></div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRefus(null)}>Annuler</Button>
            <Button onClick={() => decider(refus, "refuser", motif)}>Refuser</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- Missions ----- */}
      <Dialog open={!!misOpen} onOpenChange={(o) => { if (!o) setMisOpen(null); }}>
        <DialogContent className="sm:max-w-[680px] max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Missions : {misOpen ? nomComplet(misOpen) : ""}</DialogTitle></DialogHeader>
          <div className="rounded-lg border p-3 space-y-3 bg-muted/30">
            <p className="text-sm font-semibold">Nouvelle mission</p>
            {gestion && (
              <div><Label className="text-xs">Food truck employeur</Label>
                <select className="w-full h-9 border rounded-md px-2 text-sm" value={mForm.food_truck_id || ""}
                  onChange={(e) => setMForm((f) => ({ ...f, food_truck_id: e.target.value }))}>
                  <option value="">Choisir...</option>
                  {camions.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select></div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div><Label className="text-xs">Date</Label><Input type="date" value={mForm.date || ""} onChange={(e) => setMForm((f) => ({ ...f, date: e.target.value }))} /></div>
              <div><Label className="text-xs">Début</Label><Input type="time" value={mForm.heure_debut || ""} onChange={(e) => setMForm((f) => ({ ...f, heure_debut: e.target.value }))} /></div>
              <div><Label className="text-xs">Fin</Label><Input type="time" value={mForm.heure_fin || ""} onChange={(e) => setMForm((f) => ({ ...f, heure_fin: e.target.value }))} /></div>
              <div><Label className="text-xs">Pause (min)</Label><Input type="number" value={mForm.pause_min} onChange={(e) => setMForm((f) => ({ ...f, pause_min: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Lieu</Label><Input value={mForm.lieu || ""} placeholder="Campus HEC, marché de Gif..." onChange={(e) => setMForm((f) => ({ ...f, lieu: e.target.value }))} /></div>
              <div><Label className="text-xs">{misOpen?.statut === "auto_entrepreneur" ? "Tarif horaire HT (€)" : "Taux horaire brut (€)"}</Label>
                <Input type="number" value={mForm.taux_horaire} onChange={(e) => setMForm((f) => ({ ...f, taux_horaire: e.target.value }))} /></div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={ajouterMission}>Enregistrer la mission</Button>
              {misOpen?.statut === "auto_entrepreneur" && (monFt || mForm.food_truck_id) && (
                <Button size="sm" variant="outline"
                  onClick={() => telecharger(`/vivier/${misOpen.id}/contrat-prestation`, `contrat_prestation_${misOpen.nom || "prestataire"}.pdf`,
                    gestion ? { food_truck_id: mForm.food_truck_id || monFt } : undefined)}>
                  <FileText className="w-3.5 h-3.5 mr-1" /> Contrat-cadre de prestation
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Une fois la mission enregistrée, l'extra la retrouve dans son espace et signe son contrat en ligne.
              Pensez à faire la déclaration préalable à l'embauche (DPAE) pour un salarié.
            </p>
          </div>
          {missions && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-4 text-sm">
                <span>Total : <strong>{String(missions.total_heures).replace(".", ",")} h</strong></span>
                <span>Montant : <strong>{euros(missions.total_montant)}</strong></span>
                <span className={missions.reste_a_payer > 0 ? "text-red-600" : "text-emerald-700"}>Reste à payer : <strong>{euros(missions.reste_a_payer)}</strong></span>
              </div>
              {missions.missions.length === 0 ? (
                <p className="text-sm text-muted-foreground">{gestion ? "Aucune mission enregistrée." : "Aucune mission avec votre food truck."}</p>
              ) : missions.missions.map((m) => (
                <div key={m.id} className="flex items-center gap-2 text-sm border rounded-md p-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{dateFr(m.date)} · {m.lieu || "sans lieu"}{gestion ? ` · ${m.food_truck_nom}` : ""}</p>
                    <p className="text-xs text-muted-foreground">
                      {m.heure_debut} à {m.heure_fin}{m.pause_min ? ` (pause ${m.pause_min} min)` : ""} · {String(m.heures).replace(".", ",")} h
                      {" · "}{m.signe_le
                        ? <span className="text-emerald-700">contrat signé</span>
                        : <span className="text-amber-700">contrat à signer</span>}
                    </p>
                  </div>
                  <span className="whitespace-nowrap font-medium">{euros(m.montant)}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" title={m.paye ? "Payée" : "Marquer comme payée"} onClick={() => basculerPaye(m)}>
                    <Check className={`w-4 h-4 ${m.paye ? "text-emerald-600" : "opacity-30"}`} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" title="Contrat de cette mission"
                    onClick={() => telecharger(`/vivier/${misOpen.id}/contrat`, `contrat_${misOpen.nom || "extra"}_${m.date}.pdf`, { mission_id: m.id })}>
                    <FileText className={`w-3.5 h-3.5 ${m.signe_le ? "text-emerald-600" : ""}`} />
                  </Button>
                  {misOpen?.statut === "auto_entrepreneur" && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Bon de commande"
                      onClick={() => telecharger(`/vivier/${misOpen.id}/bon-commande`, `bon_commande_${m.date}.pdf`, { mission_id: m.id })}>
                      <ClipboardList className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" title="Supprimer" onClick={() => supprimerMission(m)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ----- Evaluation ----- */}
      <Dialog open={!!evalOpen} onOpenChange={(o) => { if (!o) setEvalOpen(null); }}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader><DialogTitle>Évaluer {evalOpen ? nomComplet(evalOpen) : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Note de 1 à 5 après une mission. Les critères laissés vides ne comptent pas.
            </p>
            {Object.entries(L_CRIT).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <Label className="flex-1">{v}</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Button key={n} type="button" size="icon" className="h-8 w-8"
                      variant={notes[k] === n ? "default" : "outline"}
                      onClick={() => setNotes((s) => ({ ...s, [k]: n }))}>{n}</Button>
                  ))}
                </div>
              </div>
            ))}
            <div><Label>Contexte</Label><Input value={notes.contexte || ""} placeholder="Service du 12/11 à HEC" onChange={(e) => setNotes((s) => ({ ...s, contexte: e.target.value }))} /></div>
            <div><Label>Commentaire</Label><Textarea rows={2} value={notes.commentaire || ""} onChange={(e) => setNotes((s) => ({ ...s, commentaire: e.target.value }))} /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEvalOpen(null)}>Annuler</Button>
            <Button onClick={noter}>Enregistrer la note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
